"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.dataIdFromObject = exports.promoteGQLErrorLink = exports.retryLink = exports.batchingLink = exports.contextErrorLinks = exports.onErrorLink = exports.errorContextLink = void 0;
/* eslint-disable import/no-extraneous-dependencies */
var retry_1 = require("@apollo/client/link/retry");
var error_1 = require("@apollo/client/link/error");
var http_1 = require("@apollo/client/link/http");
var batch_http_1 = require("@apollo/client/link/batch-http");
var core_1 = require("@apollo/client/link/core");
var errors_1 = require("@apollo/client/errors");
/* eslint-enable import/no-extraneous-dependencies */
var observability_1 = require("@opendoor/observability");
var shouldIgnoreError = function (ignorableErrorsList, errorMessage) {
    return ignorableErrorsList.find(function (error) { return errorMessage.indexOf(error.value) !== -1 || error.value.indexOf(errorMessage) !== -1; });
};
// Link that bookkeeps the originating call for an error. Creating a new
// error gives you a stack trace that corresponds to the stack at that time
// this can then be looked up later and used, instead of graphql errors pointing
// to the error reporting link.
exports.errorContextLink = new core_1.ApolloLink(function (operation, forward) {
    // create an error so we can capture the stack trace when the query is sent
    var queryStack = new Error('Query Stack');
    // remove this frame from the stack
    // so that it's not in every error message
    queryStack.stack = (queryStack.stack || '').split('\n').slice(2).join('\n');
    operation.setContext({ queryStack: queryStack });
    return forward(operation);
});
// https://www.apollographql.com/docs/react/data/error-handling/
var onErrorLink = function (_a) {
    var trackError = _a.trackError, ignorableErrorsList = _a.ignorableErrorsList;
    return error_1.onError(function (_a) {
        var graphQLErrors = _a.graphQLErrors, networkError = _a.networkError, operation = _a.operation;
        if (!graphQLErrors) {
            return;
        }
        graphQLErrors.forEach(function (graphQLError) {
            var _a, _b;
            var message = graphQLError.message, path = graphQLError.path;
            // This will intentionally ignore specified graphql errors
            var ignoreError = ignorableErrorsList
                ? shouldIgnoreError(ignorableErrorsList, "GraphQL error: " + operation.operationName + ": " + message)
                : undefined;
            var extras = __assign({ message: "GraphQL error: " + operation.operationName + ": " + message, path: path, operationName: operation.operationName, variables: operation.variables, body: (_b = (_a = operation.query.loc) === null || _a === void 0 ? void 0 : _a.source) === null || _b === void 0 ? void 0 : _b.body }, operation.getContext().linkContext);
            var apolloError = new errors_1.ApolloError({
                graphQLErrors: [graphQLError],
                networkError: networkError,
                extraInfo: extras,
            });
            var contextStack = operation.getContext().queryStack;
            if (contextStack && contextStack.stack) {
                // set the stack of the apollo error to the context query stack from
                // the errorContextLink (if available)
                apolloError.stack = contextStack.stack;
            }
            if (!ignoreError || ignoreError.allowSentry) {
                if (trackError) {
                    trackError("GraphQL error: " + operation.operationName + ": " + message, apolloError, extras);
                }
                else {
                    observability_1.Sentry.withScope(function (scope) {
                        scope.setTag('source', 'Apollo onErrorLink');
                        scope.setExtras(extras);
                        scope.setFingerprint([operation.operationName, message]);
                        observability_1.Sentry.captureException(apolloError);
                    });
                }
            }
        });
    });
};
exports.onErrorLink = onErrorLink;
var httpLink = function (uri) { return http_1.createHttpLink({ uri: uri }); };
var batchHttpLink = function (uri) {
    return new batch_http_1.BatchHttpLink({
        uri: uri,
        credentials: 'include',
        headers: {
            batch: true,
        },
    });
};
// Combination of errorContextLink and onErrorLink, used for errors that report
// the stack of the original query that failed
// use with "OnErrorArgs", and then spread into your link function
var contextErrorLinks = function (args) {
    return [exports.errorContextLink, exports.onErrorLink(args)];
};
exports.contextErrorLinks = contextErrorLinks;
// batches by default unless specifically specified otherwise (by adding `noBatch: true` to context)
var batchingLink = function (uri) {
    return core_1.split(function (operation) { return !!operation.getContext().noBatch; }, httpLink(uri), batchHttpLink(uri));
};
exports.batchingLink = batchingLink;
var retryLink = function (_a) {
    var initial = _a.initial, max = _a.max;
    return new retry_1.RetryLink({
        delay: {
            initial: initial ? initial : 500,
            max: max ? max : 3000,
            jitter: true,
        },
        attempts: {
            max: 5,
            retryIf: function (error, _operation) { return !!error; },
        },
    });
};
exports.retryLink = retryLink;
// by default RetryLink only handles "network" errors. This makes it handle all errors.
// See: https://github.com/apollographql/apollo-link/issues/541
var promoteGQLErrorLink = function (blockedRetryListStatusNumbers) {
    return new core_1.ApolloLink(function (operation, forward) {
        return forward(operation).map(function (data) {
            // @ts-expect-error fix me
            if (data && data.errors && data.errors.length > 0) {
                // @ts-expect-error fix me
                data.errors.forEach(function (error) {
                    var extensions = error.extensions;
                    if (!extensions) {
                        return;
                    }
                    if (extensions.code === 'GRPC_ERROR' &&
                        // Ensure to rethrow error if not included in blocked retry statuses list
                        (!blockedRetryListStatusNumbers ||
                            !blockedRetryListStatusNumbers.includes(extensions.exception.code))) {
                        throw error;
                    }
                });
            }
            return data;
        });
    });
};
exports.promoteGQLErrorLink = promoteGQLErrorLink;
// InMemoryCache only checks `id` and _id` fields by default
// it does not infer your unique id field from the ID type
// this implements the default InMemoryCache behavior plus
// an additional check for `uuid` which is used extensively in our application
// eslint-disable-next-line @typescript-eslint/no-explicit-any
var dataIdFromObject = function (object) {
    var __typename = object.__typename, id = object.id, _id = object._id, uuid = object.uuid;
    if (typeof __typename === 'string') {
        if (uuid !== void 0)
            return __typename + ":" + uuid;
        if (id !== void 0)
            return __typename + ":" + id;
        if (_id !== void 0)
            return __typename + ":" + _id;
    }
};
exports.dataIdFromObject = dataIdFromObject;
//# sourceMappingURL=apolloLink.js.map